import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import { ALL_COLOR_IDS, COLORS, DEFAULT_PALETTE, LAB_SAFE, textOn, type ColorId } from "../../shared/colors.ts";
import { STAGES, STATE_TAGS, type BlockKind, type CallMode, type ClientMsg, type PublicState, type ServerMsg, type StageId, type Tier } from "../../shared/types.ts";
import type { Experience } from "../../shared/parse.ts";
import { useSocket, type MeInfo } from "../lib/client.ts";
import { cue, isSpeaking, say, unlockAudio } from "../lib/cues.ts";
import { Call, type CallStatus } from "../lib/rtc.ts";
import { VoiceCapture } from "../lib/voice.ts";
import { CardWatcher, classifyPixels, loadCalibration, meanLab, sampleVideo, saveCalibration, type Calibration } from "../lib/cv.ts";

const nameOf = (me: MeInfo, id: string) => (id === me.id ? me.name : me.others.find((o) => o.id === id)?.name ?? id);

export function Room({ me }: { me: MeInfo }) {
  const [entered, setEntered] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);

  const [asking, setAsking] = useState(false);
  const enter = async () => {
    unlockAudio();
    setAsking(true);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: { width: { ideal: 960 }, height: { ideal: 720 }, facingMode: "user" },
      });
      setStream(s);
    } catch (e) {
      setMediaError(e instanceof Error ? e.message : String(e));
    }
    setEntered(true);
  };

  if (!entered) {
    return (
      <main className="enter">
        <p className="eyebrow">Session</p>
        <h1 className="display">Come into the room.</h1>
        <p className="lede">Your browser will ask for your camera and mic. The call runs directly between your browser and your partner's. Your voice is only used to catch your answers.</p>
        <button className="btn primary big" onClick={enter} disabled={asking} data-testid="enter">{asking ? "Waiting for browser permission" : "Enter with camera and mic"}</button>
        {asking && <p className="hint">Look for the permission prompt in your browser. If it's stuck, check camera access in your system settings, or come in without them.</p>}
        <button className="btn ghost" onClick={() => { unlockAudio(); setEntered(true); }}>Enter without them</button>
      </main>
    );
  }
  return <RoomInner me={me} stream={stream} mediaError={mediaError} />;
}

function RoomInner({ me, stream, mediaError }: { me: MeInfo; stream: MediaStream | null; mediaError: string | null }) {
  const [remote, setRemote] = useState<MediaStream | null>(null);
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const callRef = useRef<Call | null>(null);
  const pendingPeer = useRef<boolean | null>(null);

  const onMessage = useCallback((m: ServerMsg) => {
    if (m.t === "peer") {
      if (callRef.current) callRef.current.peerPresent(m.present);
      else pendingPeer.current = m.present;
    }
    if (m.t === "signal") void callRef.current?.signal(m.data);
  }, []);
  const { state, connected, error, send } = useSocket("room", onMessage);

  const callMode: CallMode = state?.session?.callMode ?? "builtin";
  useEffect(() => {
    if (!stream || callMode !== "builtin") return;
    const others = me.others[0]?.id ?? "";
    const call = new Call(stream, {
      caller: me.id < others,
      iceServers: me.iceServers,
      sendSignal: (data) => send({ t: "signal", data }),
      onRemote: setRemote,
      onStatus: setCallStatus,
    });
    callRef.current = call;
    if (pendingPeer.current) call.peerPresent(true);
    return () => { call.close(); callRef.current = null; setRemote(null); };
  }, [stream, callMode, me.id, me.iceServers, me.others, send]);

  const iAmGuesser = state?.block?.guesserId === me.id;
  const iAmPartner = !!state?.block && !iAmGuesser;

  // Clever Hans guard: my mic stays off while my partner is answering, whatever I'm tempted to say.
  const phase = state?.phase;
  const answering = phase === "card_up" || phase === "locking";
  const partnerShouldMute = iAmPartner && answering && callMode === "builtin";
  useEffect(() => {
    if (!stream) return;
    for (const t of stream.getAudioTracks()) t.enabled = !partnerShouldMute;
  }, [stream, partnerShouldMute]);

  // Guesser's voice ----------------------------------------------------------
  const voiceRef = useRef<VoiceCapture | null>(null);
  const trialIdRef = useRef<number | null>(null);
  trialIdRef.current = state?.trial?.id ?? null;
  const [heard, setHeard] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const testMode = useRef(false);
  const speechStartTrial = useRef<number | null>(null);
  const postClip = useCallback(async (wav: Blob) => {
    const mode = testMode.current ? "test" : "trial";
    // The trial live when speech began, so a late clip can't score the next card.
    const trial = speechStartTrial.current;
    try {
      const r = await fetch(`/api/voice?mode=${mode}${trial ? `&trial=${trial}` : ""}`, { method: "POST", body: wav, headers: { "content-type": "audio/wav" } });
      const body = await r.json();
      if (body.transcript) setHeard(body.transcript);
      if (mode === "test") testMode.current = false;
    } catch { /* The partner can still tap the answer in. */ }
  }, []);

  const needsVoice = me.voice && !!stream && (iAmGuesser || !state?.block);
  useEffect(() => {
    if (!needsVoice || !stream) return;
    const audioOnly = new MediaStream(stream.getAudioTracks().map((t) => t.clone()));
    const v = new VoiceCapture(audioOnly, {
      onSpeechStart: () => { speechStartTrial.current = trialIdRef.current; },
      onClip: (wav) => void postClip(wav),
      onLevel: (rms) => setLevel((l) => Math.max(rms, l * 0.85)),
    });
    voiceRef.current = v;
    (window as unknown as { __mlVoice?: VoiceCapture }).__mlVoice = v;
    void v.start();
    return () => { v.stop(); audioOnly.getTracks().forEach((t) => t.stop()); voiceRef.current = null; };
  }, [needsVoice, stream, postClip]);

  useEffect(() => {
    // Off while the app is speaking, and in the lobby unless a mic test is running.
    const id = setInterval(() => {
      // Only during a turn or a mic test, so ordinary chat between turns never leaves the browser.
      const inTurn = !!state?.block && state.phase !== "block_done";
      const listening = !isSpeaking() && (testMode.current || inTurn);
      voiceRef.current?.setEnabled(listening);
    }, 80);
    return () => clearInterval(id);
  }, [state?.block, state?.phase]);

  // Cues ---------------------------------------------------------------------
  const prev = useRef<PublicState | null>(null);
  useEffect(() => {
    if (!state) return;
    const was = prev.current;
    prev.current = state;
    if (!was) return;
    const t = state.trial;
    if (state.sayAgain !== was.sayAgain && iAmGuesser) { cue.retry(); say("Say again"); }
    if (state.paused && !was.paused && state.block && iAmGuesser) say("Paused. Say next when you're ready.");
    if (state.phase === was.phase && t?.guess === was.trial?.guess) return;
    if (state.phase === "card_up" && was.phase !== "card_up") cue.deal();
    if (state.phase === "locking" && t?.guess && t.guess !== was.trial?.guess && iAmGuesser) say(COLORS[t.guess].label);
    if (state.phase === "reveal" && was.phase !== "reveal" && t) {
      if (t.condition === "sham") { cue.neutral(); if (iAmGuesser) say("Control trial. Nothing was shown."); }
      else if (t.hit) { cue.hit(); if (iAmGuesser) say("Hit"); }
      else { cue.miss(); if (iAmGuesser && t.target) say(`It was ${COLORS[t.target].label}`); }
    }
    if (state.phase === "block_done" && was.phase !== "block_done") {
      cue.done();
      const b = state.block;
      if (b) say(`Turn complete. ${b.hits} hits from ${b.ribbon.filter((r) => !r.sham).length} cards.`);
    }
  }, [state, iAmGuesser]);

  // Cards mode: camera -------------------------------------------------------
  const localVideo = useRef<HTMLVideoElement | null>(null);
  const tier = state?.session?.tier;
  const cvOn = tier === "cards" && iAmGuesser && !!stream && !!state?.block && !state.paused;
  const lastPhase = useRef<string | undefined>(undefined);
  const watcher = useRef(new CardWatcher());
  useEffect(() => {
    if (!cvOn) return;
    const canvas = document.createElement("canvas");
    const cal = loadCalibration();
    const id = setInterval(() => {
      if (!localVideo.current) return;
      const px = sampleVideo(localVideo.current, canvas);
      if (!px) return;
      const seen = watcher.current.push(classifyPixels(px, cal));
      if (seen) send({ t: "cv", color: seen.color, confidence: seen.confidence });
    }, 120);
    return () => clearInterval(id);
  }, [cvOn, send]);
  useEffect(() => {
    // After each reveal, the current card has to leave the frame before another can register.
    if (lastPhase.current !== "reveal" && phase === "reveal") watcher.current.rearm();
    lastPhase.current = phase;
  }, [phase]);

  const peer = me.others[0];
  const peerHere = peer ? !!state?.presence[peer.id]?.room : false;

  if (!state) return <main className="room-loading"><p className="eyebrow">{connected ? "Syncing" : "Connecting"}</p></main>;

  return (
    <main className="room" data-phase={state.phase} data-role={iAmGuesser ? "guesser" : iAmPartner ? "partner" : "none"}>
      {error && <div className="toast" role="alert">{error}</div>}
      {mediaError && <div className="notice warn" role="alert">Camera or mic unavailable: {mediaError}. You can still run the session and tap answers in.</div>}
      <div className="room-grid">
        <section className="stage">
          <VideoStage
            remote={remote} local={stream} localRef={localVideo} peerName={peer?.name ?? "Partner"}
            callStatus={callStatus} callMode={callMode} peerHere={peerHere} partnerMuted={partnerShouldMute}
            guesserAnswering={iAmGuesser && answering} />
          {state.block && <Ribbon state={state} />}
        </section>
        <aside className="panel">
          <Panel state={state} me={me} send={send} heard={heard} level={level} stream={stream} localRef={localVideo}
            iAmGuesser={iAmGuesser} iAmPartner={iAmPartner} peerHere={peerHere}
            testVoice={() => { testMode.current = true; setHeard(null); }} />
        </aside>
      </div>
    </main>
  );
}

function VideoStage(p: {
  remote: MediaStream | null; local: MediaStream | null; localRef: React.MutableRefObject<HTMLVideoElement | null>;
  peerName: string; callStatus: CallStatus; callMode: CallMode; peerHere: boolean; partnerMuted: boolean; guesserAnswering: boolean;
}) {
  const remoteEl = useRef<HTMLVideoElement>(null);
  useEffect(() => { if (remoteEl.current) remoteEl.current.srcObject = p.remote; }, [p.remote]);
  const setLocal = useCallback((el: HTMLVideoElement | null) => {
    p.localRef.current = el;
    if (el && p.local) el.srcObject = p.local;
  }, [p.local, p.localRef]);

  const label = p.callMode === "external" ? "On your own call. Use FaceTime or Zoom for video."
    : !p.peerHere ? `Waiting for ${p.peerName}`
    : p.callStatus === "failed" ? "The call couldn't connect. Use FaceTime and choose “my own call”."
    : p.callStatus === "connected" ? "" : "Connecting call";
  return (
    <div className="video">
      {/* The guesser can't hear partner audio while answering: both ends enforce it. */}
      <video ref={remoteEl} autoPlay playsInline muted={p.guesserAnswering} className="video-remote" data-testid="remote-video" />
      {label && <div className="video-empty"><span>{label}</span></div>}
      {p.local && <video ref={setLocal} autoPlay playsInline muted className="video-self" data-testid="self-video" />}
      {p.partnerMuted && <div className="muted-chip">Your mic is off until the answer locks</div>}
    </div>
  );
}

function Ribbon({ state }: { state: PublicState }) {
  const b = state.block!;
  const cells = Array.from({ length: b.planned }, (_, i) => b.ribbon[i] ?? null);
  return (
    <div className="ribbon-live" aria-label={`${b.done} of ${b.planned} cards done`}>
      {cells.map((c, i) => (
        <span key={i} className={`rl ${c ? (c.sham ? "sham" : c.hit ? "hit" : "miss") : "todo"}`} style={c && !c.sham && c.target ? { background: COLORS[c.target].ui } : undefined} title={c ? (c.sham ? "Control" : c.hit ? "Hit" : "Miss") : ""} />
      ))}
    </div>
  );
}

function Panel(p: {
  state: PublicState; me: MeInfo; send: (m: ClientMsg) => void; heard: string | null; level: number; stream: MediaStream | null;
  localRef: React.MutableRefObject<HTMLVideoElement | null>; iAmGuesser: boolean; iAmPartner: boolean; peerHere: boolean; testVoice: () => void;
}) {
  const { state, me, send } = p;
  if (!state.session) return <StartSession me={me} send={send} peerHere={p.peerHere} />;
  if (state.phase === "lobby") return <SetupTurn {...p} />;
  if (state.phase === "block_done") return <TurnDone {...p} />;
  return <LiveTurn {...p} />;
}

// ---- session start ----------------------------------------------------------

function StartSession({ me, send, peerHere }: { me: MeInfo; send: (m: ClientMsg) => void; peerHere: boolean }) {
  const [tier, setTier] = useState<Tier>("lab");
  const [palette, setPalette] = useState<ColorId[]>(DEFAULT_PALETTE);
  const [turn, setTurn] = useState(20);
  const [callMode, setCallMode] = useState<CallMode>("builtin");
  const [witness, setWitness] = useState("");
  const toggle = (c: ColorId) => setPalette((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c]));
  const choices = tier === "lab" ? LAB_SAFE : ALL_COLOR_IDS;
  const active = palette.filter((c) => choices.includes(c));
  return (
    <div className="stack">
      <p className="eyebrow">New session</p>
      <h2 className="panel-title">{peerHere ? `${me.others[0]?.name} is here.` : "Set up the session."}</h2>
      <Field label="Deck">
        <div className="seg">
          <button className={tier === "lab" ? "on" : ""} onClick={() => setTier("lab")} data-testid="tier-lab">Phone card</button>
          <button className={tier === "cards" ? "on" : ""} onClick={() => setTier("cards")} data-testid="tier-cards">Physical cards</button>
        </div>
        <p className="hint">{tier === "lab" ? "The computer picks and your phone shows it. Your partner stays blind. Only this counts toward the verdict." : "Your own deck, read by your camera. Good practice, but kept out of the verdict because touch and shuffling can't be controlled."}</p>
      </Field>
      <Field label={`Colours, chance ${active.length ? Math.round(100 / active.length) : 0}%`}>
        <div className="chips">
          {choices.map((c) => (
            <button key={c} className={active.includes(c) ? "chip on" : "chip"} onClick={() => toggle(c)} data-testid={`palette-${c}`}>
              <i style={{ background: COLORS[c].ui }} />{COLORS[c].label}
            </button>
          ))}
        </div>
      </Field>
      <Field label={`Cards per turn: ${turn}`}>
        <input type="range" min={5} max={50} step={5} value={turn} onChange={(e) => setTurn(+e.target.value)} data-testid="turn-length" />
      </Field>
      <Field label="Call">
        <div className="seg">
          <button className={callMode === "builtin" ? "on" : ""} onClick={() => setCallMode("builtin")}>Built in</button>
          <button className={callMode === "external" ? "on" : ""} onClick={() => setCallMode("external")} data-testid="call-external">My own call</button>
        </div>
        <p className="hint">{callMode === "builtin" ? "Mutes the partner automatically until each answer locks." : "FaceTime or Zoom. Your partner has to stay quiet by choice until each answer locks."}</p>
      </Field>
      <Field label="Outside witness, if any">
        <input className="text" value={witness} onChange={(e) => setWitness(e.target.value)} placeholder="A skeptic's name" />
      </Field>
      <button className="btn primary big" disabled={active.length < 2}
        onClick={() => send({ t: "start_session", tier, palette: active, trialsPerTurn: turn, callMode, witness: witness.trim() || null })}
        data-testid="start-session">Start session</button>
    </div>
  );
}

// ---- turn setup ------------------------------------------------------------

const KINDS: { id: BlockKind; label: string; why: string }[] = [
  { id: "test", label: "Test", why: "Counts toward the verdict. Declared before the first card." },
  { id: "practice", label: "Practice", why: "Feedback, no pressure. Never counts, and can't be relabelled later." },
  { id: "audit", label: "Rig attack", why: "24 cards. Mask on and cheat as hard as you can: tilt, squint, scrunch, push it. If you beat chance, this rig leaks." },
  { id: "sighted", label: "Eyes-open check", why: "5 cards with the mask off. Should score near 100%, which proves capture and scoring work." },
];

const RIG_PRESETS = ["Mindfold", "Manta", "Mindfold + chin barrier", "Manta + chin barrier", "Hood sealed at neck"];

function SetupTurn(p: Parameters<typeof Panel>[0]) {
  const { state, me, send } = p;
  const session = state.session!;
  const people = [{ id: me.id, name: me.name, rig: me.rig }, ...me.others];
  // Swap by default: whoever didn't just guess goes next.
  const [guesser, setGuesser] = useState<string>(() => people.find((x) => x.id !== state.lastGuesserId)?.id ?? me.id);
  const [kind, setKind] = useState<BlockKind>("test");
  const [rig, setRig] = useState(state.rigs[guesser] ?? "");
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => {
    setRig(state.rigs[guesser] ?? "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guesser]);
  const isMe = guesser === me.id;
  const cardReady = !!state.presence[guesser]?.card;
  const needsCard = session.tier === "lab";
  const ready = !needsCard || cardReady;
  return (
    <div className="stack">
      <p className="eyebrow">{session.tier === "lab" ? "Phone card" : "Physical cards"} · {session.palette.length} colours</p>
      <h2 className="panel-title">Next turn</h2>
      <Field label="Who's guessing">
        <div className="seg">
          {people.map((x) => (
            <button key={x.id} className={guesser === x.id ? "on" : ""} onClick={() => setGuesser(x.id)} data-testid={`guesser-${x.id}`}>{x.name}</button>
          ))}
        </div>
      </Field>
      <Field label="What kind of turn">
        <div className="seg four">
          {KINDS.map((k) => (
            <button key={k.id} className={kind === k.id ? "on" : ""} onClick={() => setKind(k.id)} data-testid={`kind-${k.id}`}>{k.label}</button>
          ))}
        </div>
        <p className="hint">{KINDS.find((k) => k.id === kind)!.why}</p>
      </Field>
      {kind !== "sighted" && (
        <Field label="Mask and setup">
          <input className="text" value={rig} onChange={(e) => setRig(e.target.value)} placeholder="Name this rig" list="rigs" data-testid="rig" />
          <datalist id="rigs">{RIG_PRESETS.map((r) => <option key={r} value={r} />)}</datalist>
          {kind === "test" && state.rigStatus[`${guesser}:${rig.trim()}`] === "passed" ? (
            <p className="status-line good"><Icon kind="done" /> This rig has been attacked and held.</p>
          ) : kind === "test" && state.rigStatus[`${guesser}:${rig.trim()}`] === "failed" ? (
            <p className="notice">This rig beat chance under attack, so test trials on it won't count. Change the setup and give it a new name.</p>
          ) : kind === "test" ? (
            <p className="notice" data-testid="rig-warning">This rig hasn't passed an attack, so test trials on it won't count toward the verdict. Run a rig attack first.</p>
          ) : (
            <p className="hint">Rigs are tracked by name, so changing the setup means a new name and a new attack.</p>
          )}
        </Field>
      )}
      <Field label="How are you right now?">
        <div className="chips">
          {STATE_TAGS.map((t) => (
            <button key={t} className={tags.includes(t) ? "chip on" : "chip"} onClick={() => setTags((x) => (x.includes(t) ? x.filter((y) => y !== t) : [...x, t]))}>{t}</button>
          ))}
        </div>
      </Field>
      {needsCard && <CardPairing state={state} me={me} guesser={guesser} isMe={isMe} />}
      {session.tier === "cards" && isMe && <Calibrate palette={session.palette} localRef={p.localRef} />}
      {isMe && me.voice && <MicCheck level={p.level} heard={p.heard} test={p.testVoice} />}
      {!me.voice && <p className="notice warn">Voice capture is off on this server. The partner taps answers in.</p>}
      <button className="btn primary big" disabled={!ready || (kind !== "sighted" && !rig.trim())}
        onClick={() => send({ t: "start_block", guesserId: guesser, kind, rig: kind === "sighted" ? "eyes open" : rig, tags })}
        data-testid="start-turn">{ready ? `Start ${people.find((x) => x.id === guesser)?.name}'s turn` : "Waiting for the card phone"}</button>
      <button className="btn ghost" onClick={() => send({ t: "end_session" })} data-testid="end-session">End session</button>
    </div>
  );
}

function CardPairing({ state, me, guesser, isMe }: { state: PublicState; me: MeInfo; guesser: string; isMe: boolean }) {
  const paired = !!state.presence[guesser]?.card;
  const svg = useMemo(() => {
    const q = qrcode(0, "L");
    q.addData(me.cardLink);
    q.make();
    return q.createSvgTag({ cellSize: 3, margin: 0, scalable: true });
  }, [me.cardLink]);
  if (paired) return <p className="status-line good"><Icon kind="done" /> Card phone paired</p>;
  if (!isMe) return <p className="status-line wait"><Icon kind="waiting" /> Waiting for {nameOf(me, guesser)} to pair their phone</p>;
  return (
    <div className="pair">
      <div className="qr" dangerouslySetInnerHTML={{ __html: svg }} aria-label="QR code that pairs your phone as the card" />
      <div>
        <p className="pair-title">Scan with your phone</p>
        <p className="hint">It becomes the card. Prop it at brow height or higher, screen toward your face.</p>
      </div>
    </div>
  );
}

function MicCheck({ level, heard, test }: { level: number; heard: string | null; test: () => void }) {
  const [testing, setTesting] = useState(false);
  useEffect(() => { if (heard) setTesting(false); }, [heard]);
  return (
    <div className="mic">
      <div className="meter" aria-hidden><span style={{ width: `${Math.min(100, level * 900)}%` }} /></div>
      <button className="btn small" onClick={() => { setTesting(true); test(); }} data-testid="mic-test">{testing ? "Say a colour now" : "Test voice"}</button>
      {heard && <p className="hint" data-testid="mic-heard">Heard: "{heard}"</p>}
    </div>
  );
}

function Calibrate({ palette, localRef }: { palette: ColorId[]; localRef: React.MutableRefObject<HTMLVideoElement | null> }) {
  const [cal, setCal] = useState<Calibration>(() => loadCalibration());
  const canvas = useRef(document.createElement("canvas"));
  const capture = (c: ColorId) => {
    if (!localRef.current) return;
    const px = sampleVideo(localRef.current, canvas.current);
    if (!px) return;
    const next = { ...cal, [c]: meanLab(px) };
    setCal(next);
    saveCalibration(next);
  };
  const missing = palette.filter((c) => !cal[c]);
  return (
    <Field label="Teach the camera your cards">
      <p className="hint">Mask off. Fill the middle of your camera view with each card, then tap its colour. Redo this if the lighting changes.</p>
      <div className="chips">
        {palette.map((c) => (
          <button key={c} className={cal[c] ? "chip on" : "chip"} onClick={() => capture(c)} data-testid={`cal-${c}`}>
            <i style={{ background: COLORS[c].ui }} />{COLORS[c].label}{cal[c] ? " ✓" : ""}
          </button>
        ))}
      </div>
      {missing.length > 0 && <p className="hint">Without this, your partner taps in each card.</p>}
    </Field>
  );
}

// ---- live turn -------------------------------------------------------------

function LiveTurn(p: Parameters<typeof Panel>[0]) {
  const { state, me, send, iAmGuesser, iAmPartner } = p;
  const block = state.block!, session = state.session!, trial = state.trial;
  const guesserName = nameOf(me, block.guesserId);
  const kindLabel = KINDS.find((k) => k.id === block.kind)!.label;
  const [exp, setExp] = useState<Experience>("guess");
  useEffect(() => setExp("guess"), [trial?.id]);
  const palette = session.palette;
  const live = state.phase === "card_up" || state.phase === "locking";
  const isCards = session.tier === "cards";
  const num = trial ? block.done + (state.phase === "reveal" ? 0 : 1) : block.done + 1;

  // Keyboard: number keys pick colours for whoever's tapping in.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === "INPUT") return;
      const i = Number(e.key) - 1;
      if (i >= 0 && i < palette.length && (live || (isCards && state.phase === "ready" && !state.paused))) {
        send({ t: "guess", color: palette[i]!, source: iAmGuesser ? "guesser_tap" : "partner", experience: exp });
      }
      if (e.code === "Space" && (state.phase === "ready" || state.phase === "reveal")) { e.preventDefault(); send({ t: "next" }); }
    };
    addEventListener("keydown", on);
    return () => removeEventListener("keydown", on);
  }, [palette, live, isCards, state.phase, state.paused, iAmGuesser, exp, send]);

  if (state.phase === "ready" && state.paused) {
    return (
      <div className="stack">
        <p className="eyebrow">{kindLabel} · {guesserName}'s turn</p>
        <h2 className="panel-title">{block.kind === "sighted" ? "Mask off for this one." : iAmGuesser ? "Mask on, then say “go”." : `When ${guesserName} is settled, press start.`}</h2>
        {block.kind === "audit" && <p className="notice">This turn you're trying to cheat. Tilt your head, look down your nose, scrunch your face, push the mask. It only helps if you fail.</p>}
        <ul className="plain">
          <li>Say the colour out loud. "See red" means a visual, "feel red" means a knowing, and just "red" means a guess.</li>
          <li>Changed your mind? Say "no, blue" in the second after the readback.</li>
          <li>Interrupted? Say "scratch that" before you answer. Once you've answered, it counts.</li>
          {session.tier === "cards" && <li>Pull a card from the bag, hold it at brow height, face out to the camera, and don't touch the front. Drop it back and shake after each one.</li>}
        </ul>
        <p className="meta-line">{block.planned} cards · rig: {block.rig}</p>
        <button className="btn primary big" onClick={() => send({ t: "next" })} data-testid="go">{block.done ? "Resume" : "Start"}</button>
        <button className="btn ghost" onClick={() => send({ t: "end_block" })} data-testid="end-turn">End turn</button>
      </div>
    );
  }

  return (
    <div className="stack live">
      <div className="live-head">
        <p className="eyebrow">{kindLabel} · {guesserName}</p>
        <p className="score" data-testid="score"><b>{block.hits}</b> hits · card {Math.min(num, block.planned)} of {block.planned}</p>
      </div>

      {state.phase === "reveal" && trial ? (
        <RevealCard trial={trial} send={send} palette={palette} />
      ) : (
        <div className="answer-wait">
          {state.phase === "locking" && trial?.guess ? (
            <>
              <span className="tile" style={{ background: COLORS[trial.guess].ui, color: textOn(COLORS[trial.guess].ui) }} data-testid="heard-guess">{COLORS[trial.guess].label}</span>
              <p className="wait-text">{trial.experience === "saw" ? "Saw it" : trial.experience === "felt" ? "Felt it" : "Guess"}{trial.target || !isCards ? ". Locking" : ". Which card was it?"}</p>
              {trial.target || !isCards ? <span className="lockbar" key={trial.lockAt ?? 0} /> : null}
            </>
          ) : state.phase === "ready" ? (
            <p className="wait-text big">{iAmGuesser ? "Hold up the next card." : `Waiting for ${guesserName}'s next card.`}</p>
          ) : iAmGuesser ? (
            <p className="wait-text big" data-testid="listening">Listening. Say what you get.</p>
          ) : (
            <>
              <p className="wait-text big">{isCards ? `What does ${guesserName} say?` : "You don't know this card either."}</p>
              {!isCards && <p className="hint">Tap what you hear only if voice misses it.</p>}
            </>
          )}
          {p.heard && iAmGuesser && live && <p className="hint">Heard: "{p.heard}"</p>}
        </div>
      )}

      {isCards && iAmPartner && (live || (state.phase === "ready" && !state.paused)) && (
        <Field label={trial?.target ? `Card: ${COLORS[trial.target].label}${trial.targetSource === "cv" ? " (camera)" : ""}` : "Which card is up?"}>
          <div className="pick small">
            {palette.map((c) => (
              <button key={c} style={{ background: COLORS[c].ui, color: textOn(COLORS[c].ui) }} className={trial?.target === c ? "sel" : ""} onClick={() => send({ t: "target", color: c })} data-testid={`target-${c}`}>{COLORS[c].label}</button>
            ))}
          </div>
        </Field>
      )}

      {(live || (isCards && state.phase === "ready" && !state.paused)) && (
        <Field label={iAmPartner ? "They said" : "Tap your answer"}>
          <div className="pick">
            {palette.map((c, i) => (
              <button key={c} style={{ background: COLORS[c].ui, color: textOn(COLORS[c].ui) }} onClick={() => send({ t: "guess", color: c, source: iAmGuesser ? "guesser_tap" : "partner", experience: exp })} data-testid={`guess-${c}`}>
                {COLORS[c].label}<kbd>{i + 1}</kbd>
              </button>
            ))}
          </div>
          <div className="seg three">
            {(["guess", "felt", "saw"] as Experience[]).map((x) => (
              <button key={x} className={exp === x ? "on" : ""} onClick={() => setExp(x)}>{x === "guess" ? "Guess" : x === "felt" ? "Felt" : "Saw"}</button>
            ))}
          </div>
        </Field>
      )}

      <div className="row">
        {state.phase === "card_up" && <button className="btn" onClick={() => send({ t: "void", reason: "interrupted" })} data-testid="void">Void this card</button>}
        {state.paused
          ? <button className="btn" onClick={() => send({ t: "next" })} data-testid="resume">Next card</button>
          : <button className="btn" onClick={() => send({ t: "pause" })} data-testid="pause">Pause after this</button>}
        <button className="btn ghost" onClick={() => send({ t: "end_block" })} data-testid="end-turn">End turn</button>
      </div>
    </div>
  );
}

function RevealCard({ trial, send, palette }: { trial: NonNullable<PublicState["trial"]>; send: (m: ClientMsg) => void; palette: ColorId[] }) {
  const [fixing, setFixing] = useState(false);
  const sham = trial.condition === "sham";
  const c = trial.target ? COLORS[trial.target] : null;
  return (
    <div className="reveal" data-testid="reveal" data-hit={String(trial.hit)} data-sham={String(sham)}>
      {sham ? (
        <>
          <span className="tile dark">Dark</span>
          <p className="verdict-word">Control</p>
          <p className="hint">Nothing was shown on this one. You said {trial.guess ? COLORS[trial.guess].label.toLowerCase() : "nothing"}.</p>
        </>
      ) : (
        <>
          {c && <span className="tile" style={{ background: c.ui, color: textOn(c.ui) }}>{c.label}</span>}
          <p className="verdict-word">{trial.hit ? "Hit" : "Miss"}</p>
          {trial.guess && <p className="hint">You said {COLORS[trial.guess].label.toLowerCase()}{trial.transcript ? `, from "${trial.transcript}"` : ""}.</p>}
        </>
      )}
      {!fixing ? (
        <button className="linkish" onClick={() => setFixing(true)} data-testid="fix">Misheard? Add a note</button>
      ) : (
        <div className="stack tight">
          <p className="hint">What was actually said? This adds a note, and the score stays as locked. The answer is known now, so a change here could erase a miss.</p>
          <div className="pick small">
            {palette.map((x) => <button key={x} style={{ background: COLORS[x].ui, color: textOn(COLORS[x].ui) }} onClick={() => { send({ t: "correct", trialId: trial.id, field: "guess", color: x }); setFixing(false); }}>{COLORS[x].label}</button>)}
          </div>
        </div>
      )}
    </div>
  );
}

function TurnDone(p: Parameters<typeof Panel>[0]) {
  const { state, me, send } = p;
  const block = state.block!;
  const shown = block.ribbon.filter((r) => !r.sham);
  const chance = 1 / state.session!.palette.length;
  const [stage, setStage] = useState<StageId | null>(null);
  const [note, setNote] = useState("");
  return (
    <div className="stack">
      <p className="eyebrow">Turn complete · {nameOf(me, block.guesserId)}</p>
      <h2 className="panel-title" data-testid="turn-score">{block.hits} of {shown.length}</h2>
      <p className="lede small">Chance would give about {(shown.length * chance).toFixed(1)}. One turn doesn't tell you much. The total on Results does.</p>
      <Field label="What was the most you noticed?">
        <div className="chips">
          {STAGES.map((s) => <button key={s.id} className={stage === s.id ? "chip on" : "chip"} title={s.hint} onClick={() => setStage(s.id)} data-testid={`stage-${s.id}`}>{s.label}</button>)}
        </div>
      </Field>
      <Field label="Notes"><input className="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything worth remembering" /></Field>
      <button className="btn primary big" data-testid="next-turn"
        onClick={() => { if (stage) send({ t: "journal", blockId: block.id, stage, note: note || undefined }); else send({ t: "close_block" }); }}>Next turn</button>
      <button className="btn ghost" onClick={() => send({ t: "end_session" })} data-testid="end-session">End session</button>
    </div>
  );
}

// ---- small bits ------------------------------------------------------------

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="field"><p className="field-label">{label}</p>{children}</div>;
}

export function Icon({ kind }: { kind: "done" | "todo" | "failed" | "waiting" }) {
  if (kind === "done") return <svg className="ico good" viewBox="0 0 16 16" aria-hidden><path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  if (kind === "failed") return <svg className="ico bad" viewBox="0 0 16 16" aria-hidden><path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
  if (kind === "todo") return <svg className="ico warn" viewBox="0 0 16 16" aria-hidden><circle cx="8" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><circle cx="8" cy="11" r=".9" fill="currentColor" /></svg>;
  return <svg className="ico mute" viewBox="0 0 16 16" aria-hidden><circle cx="8" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2.4" /></svg>;
}
